import { type IThemePalette } from "../types";

export interface ICardChromeTokens {
  backgroundColor: string;
  restingBorderColor: string;
  hoverBorderColor: string;
  selectedBorderColor: string;
  textColor: string;
  textMutedColor: string;
  tintColor: string;
  badgeBgColor: string;
  borderRadius: number;
  actionPaddingHorizontal: number;
  actionPaddingVertical: number;
}

export interface ICardCheckboxTokens {
  sizeCompact: number;
  sizeMedium: number;
  borderRadius: number;
  restingBorderColor: string;
  selectedBgColor: string;
  selectedBorderColor: string;
  checkmarkColor: string;
}

export interface ICardTrayTokens {
  borderTopColor: string;
  titleColor: string;
  titleFontSize: number;
  titleLetterSpacing: number;
  paddingTop: number;
  gap: number;
  chipsGap: number;
}

export interface ICardTier1Tokens {
  tierName: "chip";
}

export interface ICardTier2Tokens {
  paddingHorizontal: number;
  paddingVertical: number;
  minHeight: number;
  avatarSize: number;
  titleFontSize: number;
  subTextFontSize: number;
  mediaTileSize: number;
  borderWidth: number;
}

export interface ICardTier3Tokens {
  padding: number;
  gap: number;
  avatarSize: number;
  titleFontSize: number;
  descriptionFontSize: number;
  metaFontSize: number;
  borderWidth: number;
}

export interface ICardTier4Tokens {
  padding: number;
  gap: number;
  avatarSize: number;
  titleFontSize: number;
  narrativeFontSize: number;
  stampFontSize: number;
  borderWidth: number;
}

export interface ICardTier5Tokens {
  padding: number;
  gap: number;
  borderWidth: number;
  topRightOffset: number;
}

export interface ICardTheme {
  chrome: ICardChromeTokens;
  checkbox: ICardCheckboxTokens;
  tray: ICardTrayTokens;
  tiers: {
    chip: ICardTier1Tokens;
    compact: ICardTier2Tokens;
    medium: ICardTier3Tokens;
    full: ICardTier4Tokens;
    custom: ICardTier5Tokens;
  };
}

/**
 * Pure factory generating the card component theme across all 5 tiers.
 *
 * @param palette - The active semantic theme palette.
 * @param isDark - Whether dark mode is currently active.
 * @returns Fully resolved ICardTheme.
 */
export function getCardTheme(palette: IThemePalette, isDark: boolean): ICardTheme {
  return {
    chrome: {
      backgroundColor: palette.card,
      restingBorderColor: palette.border,
      hoverBorderColor: palette.buttonHoverBorder,
      selectedBorderColor: palette.tint,
      textColor: palette.text,
      textMutedColor: palette.textMuted,
      tintColor: palette.tint,
      badgeBgColor: palette.buttonSecondary,
      borderRadius: 4,
      actionPaddingHorizontal: 6,
      actionPaddingVertical: 3,
    },
    checkbox: {
      sizeCompact: 16,
      sizeMedium: 18,
      borderRadius: 3,
      restingBorderColor: palette.border,
      selectedBgColor: palette.tint,
      selectedBorderColor: palette.tint,
      checkmarkColor: "#FFFFFF",
    },
    tray: {
      borderTopColor: palette.border,
      titleColor: palette.textMuted,
      titleFontSize: 9,
      titleLetterSpacing: 0.8,
      paddingTop: 3,
      gap: 3,
      chipsGap: 4,
    },
    tiers: {
      chip: {
        tierName: "chip",
      },
      compact: {
        paddingHorizontal: 12,
        paddingVertical: 8,
        minHeight: 46,
        avatarSize: 32,
        titleFontSize: 13,
        subTextFontSize: 11,
        mediaTileSize: 90,
        borderWidth: 1,
      },
      medium: {
        padding: 8,
        gap: 5,
        avatarSize: 38,
        titleFontSize: 14,
        descriptionFontSize: 12,
        metaFontSize: 11,
        borderWidth: 1,
      },
      full: {
        padding: 10,
        gap: 6,
        avatarSize: 38,
        titleFontSize: 16,
        narrativeFontSize: 13,
        stampFontSize: 9,
        borderWidth: 2,
      },
      custom: {
        padding: 8,
        gap: 5,
        borderWidth: 1,
        topRightOffset: 6,
      },
    },
  };
}
